import { Box } from "@mui/material";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  ReportPeriodicity,
  type IReportColumn,
  type IReportGrouping,
} from "../../../../models/report.dto";
import {
  type IReportTableRow,
  REPORT_EXECUTION_TRANSLATION_PATH,
  translateReportLabel,
} from "./reportExecutionUtils";
import ReportPrintableTable, {
  type TReportTranslate,
} from "./ReportPrintableTable";
import {
  emptySx,
  groupedReportGroupHeaderSx,
  groupedReportGroupSx,
} from "./reportResultStyles";

interface IGroupedReportResultTableProps {
  columns: IReportColumn[];
  rows: IReportTableRow[];
  grouping: IReportGrouping;
  emptyMessage: string;
  periodicity?: ReportPeriodicity | null;
}

interface IGroupedRows {
  key: string;
  value: unknown;
  rows: IReportTableRow[];
}

const EMPTY_GROUP_LABEL = "-";

const getRowValues = (row: IReportTableRow) =>
  row.values ??
  (row as IReportTableRow & { Values?: Record<string, unknown> }).Values ??
  {};

const getTextValue = (value: unknown) => {
  if (typeof value === "string") {
    return value.trim();
  }

  if (value === null || value === undefined) {
    return "";
  }

  return String(value).trim();
};

const getGroupBucketKey = (value: unknown) => {
  if (value === null || value === undefined) {
    return "__missing__";
  }

  if (typeof value === "string") {
    const trimmed = value.trim();

    return trimmed ? `string:${trimmed}` : "__empty__";
  }

  if (typeof value === "number" && Number.isFinite(value)) {
    return `number:${value}`;
  }

  if (typeof value === "boolean") {
    return `boolean:${value}`;
  }

  return `${typeof value}:${String(value)}`;
};

const getGroupDisplayValue = (value: unknown) => {
  const textValue = getTextValue(value);

  return textValue || EMPTY_GROUP_LABEL;
};

const normalizeFieldFormat = (format?: string | null) =>
  format?.trim().toLocaleLowerCase() ?? "";

const formatHeaderFieldValue = (
  value: unknown,
  label: string | null | undefined,
  format: string | null | undefined,
  language: string,
  trueLabel: string,
  falseLabel: string,
  translateLabel: (key: string, defaultValue?: string) => string,
) => {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  const normalizedFormat = normalizeFieldFormat(format);

  let formattedValue = "";

  if (typeof value === "number" && Number.isFinite(value)) {
    switch (normalizedFormat) {
      case "number":
        formattedValue = new Intl.NumberFormat(language).format(value);
        break;
      case "percentage":
        formattedValue = `${new Intl.NumberFormat(language).format(value)}%`;
        break;
      case "currency":
        formattedValue = new Intl.NumberFormat(language, {
          style: "currency",
          currency: "BRL",
        }).format(value);
        break;
      default:
        formattedValue = String(value);
        break;
    }
  } else if (typeof value === "boolean") {
    formattedValue = value ? trueLabel : falseLabel;
  } else if (typeof value === "string") {
    const textValue = value.trim();

    if (!textValue) {
      return "";
    }

    switch (normalizedFormat) {
      case "date":
      case "datetime": {
        const parsedDate = new Date(textValue);

        if (Number.isNaN(parsedDate.getTime())) {
          formattedValue = textValue;
          break;
        }

        formattedValue = new Intl.DateTimeFormat(language, {
          dateStyle: "short",
          ...(normalizedFormat === "datetime" ? { timeStyle: "short" } : {}),
        }).format(parsedDate);
        break;
      }
      default:
        formattedValue = textValue;
        break;
    }
  } else {
    formattedValue = String(value);
  }

  const resolvedLabel = label?.trim();

  if (!resolvedLabel) {
    return formattedValue;
  }

  const translatedLabel = translateLabel(resolvedLabel, resolvedLabel);

  return `${translatedLabel}: ${formattedValue}`;
};

const buildGroupedRows = (
  rows: IReportTableRow[],
  grouping: IReportGrouping,
): IGroupedRows[] => {
  const groups = new Map<string, IGroupedRows>();

  rows.forEach((row) => {
    const values = getRowValues(row);
    const groupValue = values[grouping.key];
    const bucketKey = getGroupBucketKey(groupValue);
    const existingGroup = groups.get(bucketKey);

    if (existingGroup) {
      existingGroup.rows.push(row);
      return;
    }

    groups.set(bucketKey, {
      key: bucketKey,
      value: groupValue,
      rows: [row],
    });
  });

  return Array.from(groups.values());
};

export interface IReportSection {
  key: string;
  title: string;
  rows: IReportTableRow[];
  showTotals: boolean;
  summaryOnly: boolean;
}

export const buildReportSections = (
  columns: IReportColumn[],
  rows: IReportTableRow[],
  grouping: IReportGrouping,
  t: TReportTranslate,
  language: string,
): IReportSection[] => {
  const dataRows = rows.filter((row) => row.isTotal !== true);
  const groups = buildGroupedRows(dataRows, grouping);
  const trueLabel = t("common.yes", "Sim");
  const falseLabel = t("common.no", "Nao");
  const groupLabel = grouping.label
    ? translateReportLabel(grouping.label, t) ||
      (t(REPORT_EXECUTION_TRANSLATION_PATH + "group", "Grupo") as string)
    : (t(REPORT_EXECUTION_TRANSLATION_PATH + "group", "Grupo") as string);
  const totalLabel = t(
    REPORT_EXECUTION_TRANSLATION_PATH + "table.total",
    "Total",
  ) as string;
  const hasTotalizableColumns = columns.some(
    (column) => column.isTotalizable === true,
  );

  const sections: IReportSection[] = groups.map((group) => {
    const headerFieldsText = (grouping.headerFields ?? [])
      .map((field) =>
        formatHeaderFieldValue(
          getRowValues(group.rows[0])[field.key],
          field.label,
          field.format,
          language,
          trueLabel,
          falseLabel,
          t,
        ),
      )
      .filter(Boolean)
      .join(" ");

    const resolvedGroupValue = getGroupDisplayValue(group.value);

    return {
      key: group.key,
      title: `${groupLabel}: ${resolvedGroupValue}${
        headerFieldsText ? ` (${headerFieldsText})` : ""
      }`,
      rows: group.rows,
      showTotals: grouping.showGroupTotals === true,
      summaryOnly: false,
    };
  });

  if (grouping.showGrandTotal === true && hasTotalizableColumns) {
    sections.push({
      key: "__grand_total__",
      title: totalLabel,
      rows: dataRows,
      showTotals: true,
      summaryOnly: true,
    });
  }

  return sections;
};

const GroupedReportResultTable: React.FC<IGroupedReportResultTableProps> = ({
  columns,
  rows,
  grouping,
  emptyMessage,
  periodicity,
}) => {
  const { i18n, t } = useTranslation();

  const hasDataRows = rows.some((row) => row.isTotal !== true);
  const sections = useMemo(
    () => buildReportSections(columns, rows, grouping, t, i18n.language),
    [columns, rows, grouping, t, i18n.language],
  );

  if (!hasDataRows || !columns.length) {
    return <Box sx={emptySx}>{emptyMessage}</Box>;
  }

  return (
    <Box component="section">
      {sections.map((section) => (
        <Box key={section.key} sx={groupedReportGroupSx}>
          <Box component="strong" sx={groupedReportGroupHeaderSx}>
            {section.title}
          </Box>

          <ReportPrintableTable
            columns={columns}
            emptyMessage={emptyMessage}
            periodicity={periodicity}
            rows={section.rows}
            showTotals={section.showTotals}
            summaryOnly={section.summaryOnly}
          />
        </Box>
      ))}
    </Box>
  );
};

export default GroupedReportResultTable;
